import { useState } from 'react';
import type {
  ProbeSpendLeg,
  ProbeSpendReconcileStatus,
  ProbeSpendTotals,
  ProbeSpendVerdict,
} from '@nadoutong/contracts';
import { PROBE_SPEND_DEFAULT_HOURS } from '@nadoutong/contracts';
import { useGuaranteedProbeSpend } from '../../api/guaranteedService.ts';
import { formatCompact, formatLocalTimestamp, formatUsdMicros } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Panel, Table, TableEmpty, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { formatCnyMicros, groupProbeSpend, totalsVerdict } from './probeSpendModel.ts';

const COLUMN_COUNT = 8;

/** Red: money is wrong or was never taken. Amber: correct but the wallet has not moved (yet). */
const STATUS_TONE: Record<ProbeSpendReconcileStatus, ChipTone> = {
  match: 'ok',
  mismatch: 'bad',
  unbilled: 'bad',
  no_funding_batch: 'warn',
  pending_allocation: 'warn',
  unknown: 'warn',
  uncharged: 'neutral',
};

function usd(micros: number | null): string {
  return micros === null ? '—' : formatUsdMicros(micros, false);
}

function StatusChip({ verdict, testId }: {
  readonly verdict: ProbeSpendVerdict;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const { status } = verdict;
  const tone: ChipTone = STATUS_TONE[status];
  const diffUsd = verdict.diffUsdMicros ?? 0;
  const diffCharges = verdict.diffCharges ?? 0;
  const label = status === 'match'
    ? t('guaranteed.probe_spend.status.match')
    : status === 'pending_allocation'
      ? t('guaranteed.probe_spend.status.pending', { count: String(verdict.pendingAllocation ?? 0) })
      : status === 'no_funding_batch'
        ? t('guaranteed.probe_spend.status.no_funding_batch', { count: String(verdict.overflowCharges ?? 0) })
        : status === 'unbilled'
          ? t('guaranteed.probe_spend.status.unbilled', { count: String(verdict.unbilledShots) })
          : status === 'mismatch'
            ? diffUsd !== 0
              ? t('guaranteed.probe_spend.status.mismatch', {
                diff: `${diffUsd < 0 ? '-' : ''}${formatUsdMicros(Math.abs(diffUsd), false)}`,
              })
              : t('guaranteed.probe_spend.status.mismatch_count', {
                diff: `${diffCharges < 0 ? '-' : ''}${String(Math.abs(diffCharges))}`,
              })
            : status === 'unknown'
              ? t('guaranteed.probe_spend.status.unknown')
              : t('guaranteed.probe_spend.status.uncharged');
  const hint = status === 'no_funding_batch'
    ? t('guaranteed.probe_spend.status.no_funding_batch_hint')
    : status === 'unbilled'
      ? t('guaranteed.probe_spend.status.unbilled_hint')
      : undefined;
  return (
    <Chip tone={tone} testId={testId} data-status={status} {...(hint !== undefined ? { title: hint } : {})}>
      {label}
    </Chip>
  );
}

function legVerdict(leg: ProbeSpendLeg): ProbeSpendVerdict {
  return {
    status: leg.status,
    diffUsdMicros: leg.diff_usd_micros,
    diffCharges: leg.diff_charges,
    pendingAllocation: leg.pending_allocation,
    overflowCharges: leg.overflow_charges,
    unbilledShots: leg.unpriced_shots,
  };
}

function SpendCells({ totals, testId }: { readonly totals: ProbeSpendTotals; readonly testId: string }): JSX.Element {
  const t = useT();
  return (
    <>
      <Td num data-testid={`${testId}-shots`}>
        <div>{totals.shots}</div>
        {totals.pre_fix_shots > 0 ? (
          <div className="text-micro text-paper-low" data-testid={`${testId}-pre-fix`}>
            {t('guaranteed.probe_spend.pre_fix', { count: String(totals.pre_fix_shots) })}
          </div>
        ) : null}
        {totals.unpriced_shots > 0 ? (
          <div className="text-micro text-warn" data-testid={`${testId}-unpriced`}>
            {t('guaranteed.probe_spend.unpriced', { count: String(totals.unpriced_shots) })}
          </div>
        ) : null}
      </Td>
      <Td num>{formatCompact(totals.input_tokens)} / {formatCompact(totals.output_tokens)}</Td>
      <Td num data-testid={`${testId}-logged`}>{usd(totals.logged_cost_usd_micros)}</Td>
      <Td num data-testid={`${testId}-charged`}>
        {usd(totals.charged_usd_micros)}
        {totals.charges !== null ? (
          <div className="text-micro text-paper-low">
            {t('guaranteed.probe_spend.count', { count: String(totals.charges) })}
          </div>
        ) : null}
      </Td>
      <Td num emphasis data-testid={`${testId}-procurement`}>{usd(totals.procurement_cost_usd_micros)}</Td>
      <Td num emphasis data-testid={`${testId}-allocated`}>
        {totals.allocated_cny_micros === null ? '—' : formatCnyMicros(totals.allocated_cny_micros)}
        {totals.allocated_charges !== null ? (
          <div className="text-micro font-normal text-paper-low">
            {t('guaranteed.probe_spend.count', { count: String(totals.allocated_charges) })}
          </div>
        ) : null}
      </Td>
    </>
  );
}

function LegRowView({ leg, testId }: { readonly leg: ProbeSpendLeg; readonly testId: string }): JSX.Element {
  return (
    <Tr interactive={false} data-testid={testId}>
      <Td className="pl-9">
        <div className="text-paper">{leg.upstream_name ?? leg.upstream_id}</div>
        <div className="font-mono text-micro text-paper-low">
          {[leg.channel_slug ?? leg.channel_id, leg.models].filter((part) => part !== null && part !== '').join(' · ')}
        </div>
      </Td>
      <SpendCells totals={leg} testId={testId} />
      <Td><StatusChip verdict={legVerdict(leg)} testId={`${testId}-status`} /></Td>
    </Tr>
  );
}

function SqlBlock({ label, sql, testId }: { readonly label: string; readonly sql: string; readonly testId: string }): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-label text-paper-mid">{label}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { void navigator.clipboard?.writeText(sql); }}
          data-testid={`${testId}-copy`}
        >
          {t('guaranteed.probe_spend.copy')}
        </Button>
      </div>
      <pre
        className="m-0 max-h-72 overflow-auto rounded border border-line-soft bg-raised/40 p-2 font-mono text-micro text-paper-mid"
        data-testid={testId}
      >
        {sql}
      </pre>
    </div>
  );
}

export function ProbeSpendCard(): JSX.Element {
  const t = useT();
  const query = useGuaranteedProbeSpend(PROBE_SPEND_DEFAULT_HOURS);
  // Collapsed by SKU by default (user 2026-09-23): total + one subtotal per SKU.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const data = query.data;
  const groups = data === undefined ? [] : groupProbeSpend(data.legs);
  const tokens = groups.map((group) => (group.key === '' ? 'none' : group.key));
  const allOpen = tokens.length > 0 && tokens.every((token) => expanded.has(token));
  const toggle = (key: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  return (
    <Panel
      testId="guaranteed-probe-spend-card"
      caption={(
        <span className="flex flex-col gap-0.5">
          <span className="inline-flex items-baseline gap-2">
            <span>{t('guaranteed.probe_spend.title', { hours: String(data?.hours ?? PROBE_SPEND_DEFAULT_HOURS) })}</span>
            {data !== undefined ? (
              <span className="text-label font-normal text-paper-low">
                {t('guaranteed.probe_spend.window', {
                  since: formatLocalTimestamp(data.since),
                  until: formatLocalTimestamp(data.until),
                })}
              </span>
            ) : null}
          </span>
          <span className="text-label font-normal text-paper-low" data-testid="guaranteed-probe-spend-wallet-note">
            {t('guaranteed.probe_spend.wallet_note')}
          </span>
        </span>
      )}
      actions={groups.length > 0 ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setExpanded(allOpen ? new Set() : new Set(tokens))}
          data-testid="guaranteed-probe-spend-toggle-all"
        >
          {allOpen ? t('guaranteed.probe_spend.collapse_all') : t('guaranteed.probe_spend.expand_all')}
        </Button>
      ) : undefined}
    >
      <Table className="rounded-none border-0 bg-transparent" tableClassName="min-w-[1080px]">
        <THead>
          <Tr interactive={false}>
            <Th>{t('guaranteed.probe_spend.col.leg')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.shots')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.tokens')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.logged')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.charged')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.procurement')}</Th>
            <Th num>{t('guaranteed.probe_spend.col.allocated')}</Th>
            <Th>{t('guaranteed.probe_spend.col.status')}</Th>
          </Tr>
        </THead>
        <TBody>
          {query.isError ? (
            <TableEmpty colSpan={COLUMN_COUNT}>{t('guaranteed.probe_spend.error')}</TableEmpty>
          ) : data === undefined ? (
            <TableEmpty colSpan={COLUMN_COUNT}>{t('guaranteed.probe_spend.loading')}</TableEmpty>
          ) : (
            <>
              <Tr interactive={false} className="bg-raised/60" data-testid="guaranteed-probe-spend-total">
                <Td emphasis>{t('guaranteed.probe_spend.total')}</Td>
                <SpendCells totals={data.totals} testId="guaranteed-probe-spend-total" />
                <Td>
                  <StatusChip
                    verdict={totalsVerdict(data.totals, data.legs)}
                    testId="guaranteed-probe-spend-total-status"
                  />
                </Td>
              </Tr>
              {groups.length === 0 ? (
                <TableEmpty colSpan={COLUMN_COUNT}>{t('guaranteed.probe_spend.empty')}</TableEmpty>
              ) : groups.map((group) => {
                const token = group.key === '' ? 'none' : group.key;
                const open = expanded.has(token);
                return [
                  <Tr
                    key={`sku:${token}`}
                    interactive={false}
                    className="bg-raised/30"
                    data-testid={`guaranteed-probe-spend-sku-${token}`}
                  >
                    <Td emphasis>
                      <button
                        type="button"
                        className="text-left"
                        aria-expanded={open}
                        onClick={() => toggle(token)}
                        data-testid={`guaranteed-probe-spend-sku-${token}-toggle`}
                      >
                        {open ? '▾' : '▸'} {group.skuName ?? group.skuId ?? t('guaranteed.probe_spend.no_sku')}
                        <span className="ml-2 text-micro font-normal text-paper-low">
                          {t('guaranteed.probe_spend.leg_count', { count: String(group.legs.length) })}
                        </span>
                      </button>
                    </Td>
                    <SpendCells totals={group.subtotal} testId={`guaranteed-probe-spend-sku-${token}`} />
                    <Td>
                      <StatusChip verdict={group.verdict} testId={`guaranteed-probe-spend-sku-${token}-status`} />
                    </Td>
                  </Tr>,
                  ...(open
                    ? group.legs.map((leg, index) => (
                      <LegRowView
                        key={`leg:${token}:${leg.upstream_id}:${leg.channel_id ?? ''}`}
                        leg={leg}
                        testId={`guaranteed-probe-spend-leg-${token}-${String(index)}`}
                      />
                    ))
                    : []),
                ];
              })}
            </>
          )}
        </TBody>
      </Table>
      {data !== undefined ? (
        <details className="border-t border-line-soft px-3 py-2" data-testid="guaranteed-probe-spend-footnote">
          <summary className="cursor-pointer text-label font-medium text-paper-mid">
            {t('guaranteed.probe_spend.reconcile.title')}
          </summary>
          <div className="mt-2 flex flex-col gap-3">
            <p className="m-0 text-micro text-paper-low">{t('guaranteed.probe_spend.reconcile.columns')}</p>
            <SqlBlock
              label={t('guaranteed.probe_spend.reconcile.log_sql')}
              sql={data.log_sql}
              testId="guaranteed-probe-spend-log-sql"
            />
            {data.ledger_sql !== null ? (
              <SqlBlock
                label={t('guaranteed.probe_spend.reconcile.ledger_sql')}
                sql={data.ledger_sql}
                testId="guaranteed-probe-spend-ledger-sql"
              />
            ) : (
              <p className="m-0 text-micro text-warn" data-testid="guaranteed-probe-spend-ledger-missing">
                {t('guaranteed.probe_spend.reconcile.ledger_missing')}
              </p>
            )}
          </div>
        </details>
      ) : null}
    </Panel>
  );
}
